/**
 * ContactPage — mirrors the contact block at the bottom of the
 * homepage (social icons row + email).  Standalone studio-themed
 * page reachable from the NavPane.
 */
import { useNavigate } from "react-router-dom";
import TopBar from "@/components/TopBar";
import NavPane from "@/components/NavPane";
import {
  YoutubeIcon, SoundcloudIcon, TwitterXIcon, LinkedInIcon, GithubIcon, FacebookIcon,
} from "@/components/SocialIcons";

const PAL = {
  bg: "#0F0E0C",
  ink: "#F5F1E8",
  muted: "#9C8E72",
  accent: "#E5C870",
  border: "rgba(229,200,112,0.18)",
};
const TIMES = "'Times New Roman',Times,serif";

const SocialLink = ({ href, label, children, testId }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    title={label}
    className="inline-flex items-center justify-center w-12 h-12 rounded-sm border transition-all"
    style={{ borderColor: "rgba(229,200,112,0.3)", color: "#E5C870" }}
    onMouseEnter={(e) => { e.currentTarget.style.color = "#F5D98A"; e.currentTarget.style.borderColor = "rgba(229,200,112,0.7)"; }}
    onMouseLeave={(e) => { e.currentTarget.style.color = "#E5C870"; e.currentTarget.style.borderColor = "rgba(229,200,112,0.3)"; }}
    data-testid={testId}
  >
    {children}
  </a>
);

export default function ContactPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen" style={{ background: PAL.bg, color: PAL.ink }} data-testid="contact-page">
      <TopBar theme="studio" onBack={() => navigate("/")} backLabel="HOME" testId="contact-back-button" />
      <div className="relative z-10 max-w-7xl mx-auto flex flex-col md:flex-row">
        <div className="md:w-[260px] md:shrink-0 md:min-h-screen flex flex-col pt-4 md:pt-6 px-4 md:px-6 pb-6 md:border-r" style={{ borderColor: PAL.border }}>
          <NavPane theme="studio" />
        </div>
        <main className="flex-1 flex flex-col pt-4 md:pt-6 px-6 md:px-12 pb-10">
          <div className="max-w-3xl">
            <h1
              className="leading-[1.02] mb-7"
              style={{
                fontFamily: TIMES,
                fontWeight: 500,
                color: PAL.accent,
                fontSize: "clamp(2.4rem, 5vw, 5rem)",
                letterSpacing: "0.005em",
                textShadow: "0 2px 0 rgba(0,0,0,0.55), 0 0 28px rgba(229,200,112,0.22)",
              }}
              data-testid="contact-title"
            >
              Contacts and Other Portfolios
            </h1>

            <div className="flex items-center gap-3 mb-6" data-testid="contact-social-row">
              <SocialLink href="https://www.youtube.com/@andrewgreen1970" label="YouTube" testId="contact-youtube-link">
                <YoutubeIcon size={20} />
              </SocialLink>
              <SocialLink href="https://soundcloud.com/andrewgreenofficial" label="SoundCloud" testId="contact-soundcloud-link">
                <SoundcloudIcon size={20} />
              </SocialLink>
              <SocialLink href="https://x.com/andrewgreenoff" label="X (Twitter)" testId="contact-twitter-link">
                <TwitterXIcon size={18} />
              </SocialLink>
              <SocialLink href="https://www.linkedin.com/in/andrew-green-572193146/" label="LinkedIn" testId="contact-linkedin-link">
                <LinkedInIcon size={20} />
              </SocialLink>
              <SocialLink href="https://github.com/andrewgreen76/" label="GitHub" testId="contact-github-link">
                <GithubIcon size={20} />
              </SocialLink>
              <SocialLink href="https://www.facebook.com/andrewgreenunofficial" label="Facebook" testId="contact-facebook-link">
                <FacebookIcon size={20} />
              </SocialLink>
            </div>

            <a
              href="mailto:andrewgreenofficial@gmail.com"
              className="inline-block"
              style={{
                color: PAL.accent,
                fontFamily: TIMES,
                letterSpacing: "0.04em",
                textDecoration: "underline",
                textUnderlineOffset: "4px",
                textTransform: "lowercase",
                fontSize: "12pt",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#F5D98A")}
              onMouseLeave={(e) => (e.currentTarget.style.color = PAL.accent)}
              data-testid="contact-email-link"
            >
              andrewgreenofficial@gmail.com
            </a>

            <table
              className="mt-8 border-collapse"
              style={{
                borderColor: PAL.border,
                fontFamily: TIMES,
                color: PAL.ink,
                fontSize: "1rem",
              }}
              data-testid="contact-discord-table"
            >
              <thead>
                <tr>
                  <th
                    style={{
                      border: `1px solid ${PAL.border}`,
                      padding: "0.55rem 1rem",
                      color: PAL.accent,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      textAlign: "left",
                    }}
                  >
                    Platform
                  </th>
                  <th
                    style={{
                      border: `1px solid ${PAL.border}`,
                      padding: "0.55rem 1rem",
                      color: PAL.accent,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      textAlign: "left",
                    }}
                  >
                    Handle
                  </th>
                  <th
                    style={{
                      border: `1px solid ${PAL.border}`,
                      padding: "0.55rem 1rem",
                      color: PAL.accent,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      textAlign: "left",
                    }}
                  >
                    Server
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr data-testid="contact-discord-row">
                  <td
                    style={{
                      border: `1px solid ${PAL.border}`,
                      padding: "0.55rem 1rem",
                      color: PAL.ink,
                    }}
                  >
                    Discord
                  </td>
                  <td
                    style={{
                      border: `1px solid ${PAL.border}`,
                      padding: "0.55rem 1rem",
                      color: PAL.ink,
                    }}
                    data-testid="contact-discord-handle"
                  >
                    agreengr
                  </td>
                  <td
                    style={{
                      border: `1px solid ${PAL.border}`,
                      padding: "0.55rem 1rem",
                      color: PAL.ink,
                    }}
                    data-testid="contact-discord-server"
                  >
                    Game Dev League, Game Dev Network
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </main>
      </div>
    </div>
  );
}
